import { employmentFieldsRequired } from 'auth/profileOptions';
import { ChoiceSet, SelectInput, TextInput } from 'shared/components/Input';
import { SkillsInput } from 'shared/components/SkillsInput';

function OptionSelect({
  id,
  label,
  error,
  required,
  value,
  options,
  placeholder,
  disabled,
  onChange,
}) {
  return (
    <SelectInput
      id={id}
      label={label}
      error={error}
      required={required}
      value={value || ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{placeholder}</option>
      {(options || []).map((option) => (
        <option key={option.code} value={option.code}>{option.label}</option>
      ))}
    </SelectInput>
  );
}

export function ProfileFormFields({
  t,
  values,
  options,
  errors = {},
  disabled = false,
  requireSkills = false,
  onChange,
}) {
  const showEmploymentFields = employmentFieldsRequired(values.is_employed, options)
    || values.is_employed === true;
  const limits = options?.limits || {};

  return (
    <div className="profile-fields">
      <div className="field-span-2">
        <TextInput
          id="full_name"
          label={t('profile.fullName')}
          hint={t('auth.fullNameHint')}
          error={errors.full_name}
          required
          value={values.full_name}
          disabled={disabled}
          autoComplete="name"
          onChange={(event) => onChange('full_name', event.target.value)}
        />
      </div>
      <OptionSelect
        id="age_range"
        label={t('profile.ageRange')}
        error={errors.age_range}
        required
        value={values.age_range}
        options={options?.age_ranges}
        placeholder={t('profile.selectPlaceholder')}
        disabled={disabled}
        onChange={(value) => onChange('age_range', value)}
      />
      <OptionSelect
        id="education_level"
        label={t('profile.educationLevel')}
        error={errors.education_level}
        required
        value={values.education_level}
        options={options?.education_levels}
        placeholder={t('profile.selectPlaceholder')}
        disabled={disabled}
        onChange={(value) => onChange('education_level', value)}
      />
      <ChoiceSet
        id="is_employed"
        label={t('profile.isEmployed')}
        error={errors.is_employed}
        required
      >
        <label>
          <input
            id="is_employed-true"
            type="radio"
            name="is_employed"
            value="true"
            checked={values.is_employed === true}
            disabled={disabled}
            onChange={() => onChange('is_employed', true)}
          />
          {t('profile.yes')}
        </label>
        <label>
          <input
            id="is_employed-false"
            type="radio"
            name="is_employed"
            value="false"
            checked={values.is_employed === false}
            disabled={disabled}
            onChange={() => onChange('is_employed', false)}
          />
          {t('profile.no')}
        </label>
      </ChoiceSet>
      {showEmploymentFields ? (
        <>
          <OptionSelect
            id="work_experience"
            label={t('profile.workExperience')}
            error={errors.work_experience}
            required
            value={values.work_experience}
            options={options?.work_experience}
            placeholder={t('profile.selectPlaceholder')}
            disabled={disabled}
            onChange={(value) => onChange('work_experience', value)}
          />
          <TextInput
            id="experience_field"
            label={t('profile.experienceField')}
            error={errors.experience_field}
            required
            value={values.experience_field}
            disabled={disabled}
            onChange={(event) => onChange('experience_field', event.target.value)}
          />
        </>
      ) : null}
      <SkillsInput
        id="skills"
        label={t('profile.skills')}
        hint={t('profile.skillsHint')}
        error={errors.skills}
        required={requireSkills}
        value={values.skills}
        maxItems={limits.skills?.max_items}
        maxLength={limits.skills?.max_length}
        disabled={disabled}
        addLabel={t('profile.skillsAdd')}
        removeLabel={t('profile.removeSkill')}
        addedAnnouncement={t('profile.skillAdded')}
        removedAnnouncement={t('profile.skillRemoved')}
        placeholder={t('profile.skillsPlaceholder')}
        onChange={(skills) => onChange('skills', skills)}
      />
      <OptionSelect
        id="country"
        label={t('profile.country')}
        error={errors.country}
        required
        value={values.country}
        options={options?.countries}
        placeholder={t('profile.selectPlaceholder')}
        disabled={disabled}
        onChange={(value) => onChange('country', value)}
      />
    </div>
  );
}

export function ProfileReadonlyFields({ t, user, options }) {
  function optionLabel(list, code) {
    if (!code) {
      return '—';
    }
    return (list || []).find((item) => item.code === code)?.label || code;
  }

  return (
    <dl className="detail-list profile-summary">
      <div>
        <dt>{t('profile.ageRange')}</dt>
        <dd>{optionLabel(options?.age_ranges, user?.age_range)}</dd>
      </div>
      <div>
        <dt>{t('profile.educationLevel')}</dt>
        <dd>{optionLabel(options?.education_levels, user?.education_level)}</dd>
      </div>
      <div>
        <dt>{t('profile.isEmployed')}</dt>
        <dd>
          {user?.is_employed === true ? t('profile.yes') : (user?.is_employed === false ? t('profile.no') : '—')}
        </dd>
      </div>
      {user?.is_employed === true ? (
        <>
          <div>
            <dt>{t('profile.workExperience')}</dt>
            <dd>{optionLabel(options?.work_experience, user?.work_experience)}</dd>
          </div>
          <div>
            <dt>{t('profile.experienceField')}</dt>
            <dd>{user?.experience_field || '—'}</dd>
          </div>
        </>
      ) : null}
      <div className="field-span-2">
        <dt>{t('profile.skills')}</dt>
        <dd>
          {Array.isArray(user?.skills) && user.skills.length > 0 ? (
            <div className="chip-row">
              {user.skills.map((skill) => (
                <span key={skill} className="chip">{skill}</span>
              ))}
            </div>
          ) : '—'}
        </dd>
      </div>
      <div>
        <dt>{t('profile.country')}</dt>
        <dd>{optionLabel(options?.countries, user?.country)}</dd>
      </div>
    </dl>
  );
}
